import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { VITE_ENV } from 'config';
import loggerService, { LoggerLevel } from '../log';
import { toRoutePattern } from './routePattern';
import { COOKIEYES_BANNER_LOAD, COOKIEYES_CONSENT_UPDATE } from '.';

/**
 * Emits one `$pageview` per client-side navigation, carrying a masked
 * `route_pattern` super property.
 *
 * Must be rendered inside both the router and `PostHogProvider`.
 *
 * Why this component owns the capture, rather than posthog-js doing it:
 *
 * - **Ordering.** `capture_pageview: 'history_change'` patches
 *   `pushState`/`replaceState` and captures *synchronously*, before any React
 *   effect runs, so a property registered from an effect always lost the race —
 *   every pageview carried the *previous* route's `route_pattern`, and the first
 *   carried none. Registering immediately before capturing here fixes that, and
 *   `capture_pageview` is `false` in App.tsx so this stays the only source of
 *   pageviews (otherwise each navigation is counted twice).
 * - **Masking.** `$pathname` is unusably high-cardinality on the parameterised
 *   asset routes, hence `route_pattern`; see `routePattern.ts`.
 * - **Consent.** Capture is opted out by default until CookieYes resolves, and a
 *   pageview sent before that is silently dropped — losing the landing screen of
 *   every visit. So a pageview stays *pending* until `is_capturing()` is true,
 *   and a CookieYes event retries it. The retry is gated on `pendingRef` because
 *   consent events are not navigations and CookieYes emits banner-load and may
 *   re-emit consent-update; retrying on each would inflate the count. Pending is
 *   cleared once the attempt is made — including when it throws, so a consent
 *   event cannot replay it — and set again on the next navigation. Registering
 *   the property early is safe: it is not an event, so it is simply already
 *   correct when the deferred pageview fires.
 * - **Region.** Analytics is hard-disabled outside the US (GDPR); feature flags
 *   are unaffected, so the guard below skips only the pageview.
 */
export default function PostHogRouteTracker() {
  const { pathname } = useLocation();
  const [retry, setRetry] = useState(0);
  const pendingRef = useRef(true);

  useEffect(() => {
    const onConsentChange = () => {
      if (pendingRef.current) setRetry((n) => n + 1);
    };

    document.addEventListener(COOKIEYES_BANNER_LOAD, onConsentChange);
    document.addEventListener(COOKIEYES_CONSENT_UPDATE, onConsentChange);

    return () => {
      document.removeEventListener(COOKIEYES_BANNER_LOAD, onConsentChange);
      document.removeEventListener(COOKIEYES_CONSENT_UPDATE, onConsentChange);
    };
  }, []);

  useEffect(() => {
    pendingRef.current = true;
  }, [pathname]);

  useEffect(() => {
    if (!VITE_ENV.posthog.analyticsEnabled) return;
    if (!pendingRef.current) return;

    try {
      posthog.register({ route_pattern: toRoutePattern(pathname) });
      if (!posthog.is_capturing()) return;

      posthog.capture('$pageview');
      pendingRef.current = false;
    } catch (error) {
      pendingRef.current = false;
      loggerService.log(LoggerLevel.ERROR, 'PostHog pageview capture failed:', error);
    }
  }, [pathname, retry]);

  return null;
}
